import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Dumbbell,
  Monitor,
  Map,
  User,
  History,
  Users,
  PersonStanding,
  Settings,
  ChevronDown,
  ChevronRight,
  Sparkles,
  CalendarDays,
  SlidersHorizontal,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}

interface NavGroup {
  icon: React.ReactNode;
  label: string;
  children: NavItem[];
}

const navItems: NavItem[] = [
  { to: '/', icon: <Home className="w-5 h-5" />, label: 'Főoldal' },
  { to: '/workout', icon: <Dumbbell className="w-5 h-5" />, label: 'Edzés' },
  { to: '/workout-builder', icon: <Sparkles className="w-5 h-5" />, label: 'AI Edzés' },
  { to: '/training-plan', icon: <CalendarDays className="w-5 h-5" />, label: 'Edzésterv', disabled: true },
  { to: '/history', icon: <History className="w-5 h-5" />, label: 'Előzmények' },
  { to: '/community', icon: <Users className="w-5 h-5" />, label: 'Közösség' },
  { to: '/floorplan', icon: <Map className="w-5 h-5" />, label: 'Teremtérkép' },
  { to: '/profile', icon: <User className="w-5 h-5" />, label: 'Profil' },
];

const settingsGroup: NavGroup = {
  icon: <Settings className="w-5 h-5" />,
  label: 'Beállítások',
  children: [
    { to: '/machines', icon: <Monitor className="w-5 h-5" />, label: 'Gépek' },
    { to: '/bodyweight', icon: <PersonStanding className="w-5 h-5" />, label: 'Saját súlyos' },
    { to: '/ai-settings', icon: <SlidersHorizontal className="w-5 h-5" />, label: 'AI beállítások' },
  ],
};

export const Sidebar = () => {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const location = useLocation();
  
  // Check if any child of settings group is active
  const isSettingsActive = settingsGroup.children.some(
    (item) => location.pathname === item.to
  );
  
  // Settings menu is open by default
  const [settingsOpen, setSettingsOpen] = useState(true);

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 font-bold text-xl text-gray-900 dark:text-gray-100">
              <Dumbbell className="w-8 h-8 text-primary-600" />
              <span>Pain-Gain</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.to}>
                  {item.disabled ? (
                    <span className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50">
                      {item.icon}
                      <span>{item.label}</span>
                      <span className="ml-auto text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">Hamarosan</span>
                    </span>
                  ) : (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </NavLink>
                  )}
                </li>
              ))}
              
              {/* Settings Group */}
              <li>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isSettingsActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {settingsGroup.icon}
                    <span>{settingsGroup.label}</span>
                  </div>
                  {settingsOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {/* Settings Children */}
                {settingsOpen && (
                  <ul className="mt-1 ml-4 space-y-1">
                    {settingsGroup.children.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`
                          }
                          onClick={() => setSidebarOpen(false)}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </nav>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              &copy; {new Date().getFullYear()} Pain-Gain
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};
